import React from 'react';
import { Box, Divider, styled } from '@mui/material';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import StaticsCoverages from '../../_global/endorsements/comparisons/statics/coverages.statics.json';
import CheckBoxSelectedIcon from '../../../_assets/svg/CheckBoxSelectedIcon.svg';
import { moneyFormat } from '../../../utils/appUtils';
import { getConsolidatedP100CoverageKeys } from '../../workflows/quotes/prime-100/utils/coverages';
import { BoostRaterVersionResponse } from '../../workflows/quotes/prime-100/api/types';
import { P100_COVERAGES } from '../../workflows/quotes/prime-100/test/fixtures/p100-coverages.fixture';
import { calculateCoverageOptionsV6 } from '../../_statics/coverage.statics.v6';
import { PRIME_100_COVERAGES } from '../../workflows/quotes/prime-100/api/constants';
import { buildSubCoverageRows } from '../../workflows/quotes/prime-100/utils';

export default function PolicyHolderQuotePolicy100Coverages({
  coverages,
  boostVersion: boostVersionOverride,
}: any) {
  const p100CoverageMapping = useSelector(
    (reduxState: { auth?: { P_100?: Record<string, unknown> } }) =>
      reduxState.auth?.P_100
  );

  // Prefer the version actually recorded on the quote (mothership).
  const boostVersion = boostVersionOverride;

  const v6CoverageContext = React.useMemo(
    () => ({
      coverageMapping: p100CoverageMapping,
    }),
    [p100CoverageMapping]
  );

  const uiCoverages = usePolicyCoverages(
    coverages,
    boostVersion,
    v6CoverageContext
  );

  return (
    <>
      <HeaderContainer>
        <Header>Coverages Details And Limits</Header>
      </HeaderContainer>
      <>
        <HeaderContainer>
          <Divider />
          <HeaderRow>
            <HeaderCell width="37%" justifyContent="flex-end">
              <HeaderText>Liability Expense</HeaderText>
            </HeaderCell>
            <DividerWithPadding padding={0} />
            <HeaderCell width="17%">
              <HeaderText>Coverage Limit</HeaderText>
            </HeaderCell>
            <HeaderCell width="17%">
              <HeaderText>Deductible</HeaderText>
            </HeaderCell>
            <HeaderCell width="12%">
              <HeaderText>Waiting Period</HeaderText>
            </HeaderCell>
            <HeaderCell width="17%">
              <HeaderText>Retro Active Period</HeaderText>
            </HeaderCell>
          </HeaderRow>
        </HeaderContainer>
        <BodyContainer>
          {uiCoverages.map((uiCoverage) => {
            if (uiCoverage.checked) {
              return (
                <BodyRow key={`${uiCoverage.coverageName}-${uiCoverage.order}`}>
                  <BodyCell
                    width="37%"
                    justifyContent="flex-end"
                    style={{ textAlign: 'right' }}
                  >
                    <Text>{uiCoverage.coverageName}</Text>
                  </BodyCell>
                  <DividerWithPadding padding={0} />
                  <BodyCell width="17%">
                    <CheckBoxSelectedIcon />
                  </BodyCell>
                </BodyRow>
              );
            }

            return (
              <BodyRow key={`${uiCoverage.coverageName}-${uiCoverage.order}`}>
                <BodyCell
                  width="37%"
                  justifyContent="flex-end"
                  style={{ textAlign: 'right' }}
                >
                  <Text>{uiCoverage.coverageName}</Text>
                </BodyCell>
                <DividerWithPadding padding={0} />
                <BodyCell width="17%">
                  <Text>{uiCoverage.limit || '-'}</Text>
                </BodyCell>
                <BodyCell width="17%">
                  <Text>{uiCoverage.deductible || '-'}</Text>
                </BodyCell>
                <BodyCell width="12%" justifyContent="center">
                  <Text>{uiCoverage.waitingPeriod || '-'}</Text>
                </BodyCell>
                <BodyCell width="17%" justifyContent="center">
                  <Text>{uiCoverage.retroactivePeriod || '-'}</Text>
                </BodyCell>
              </BodyRow>
            );
          })}
        </BodyContainer>
      </>
    </>
  );
}

const DividerWithPadding = ({ padding = 1, orientation = 'vertical' }) => {
  return (
    <FlexBox pt={padding} pb={padding}>
      {/* @ts-expect-error TS(2769): No overload matches this call. */}
      <Divider orientation={orientation} flexItem />
    </FlexBox>
  );
};

const HeaderContainer = styled(Box)(({ theme: { palette } }) => ({
  backgroundColor: palette.background.default,
  width: '100%',
  borderTopRightRadius: 4,
  borderTopLeftRadius: 4,
  padding: '0',
}));

const BodyContainer = styled(Box)({
  width: '100%',
  borderBottomRightRadius: 4,
  borderBottomLeftRadius: 4,
  padding: 0,
  overflow: 'hidden',
});

const Header = styled('h3')(({ theme: { config } }) => ({
  margin: 0,
  fontSize: config.textSizes.tertia,
  fontWeight: config.weights.normal,
  padding: '0.5rem',
}));

const FlexBox = styled(Box)(() => ({
  display: 'flex',
}));

const HeaderRow = styled(FlexBox)(({ theme: { palette } }) => ({
  display: 'flex',
  width: '100%',
  minHeight: '4rem',
  backgroundColor: palette.background.default,
}));

// @ts-expect-error TS(2769): No overload matches this call.
const HeaderCell = styled(FlexBox)(({ justifyContent, theme: { config } }) => ({
  textAlign: 'center',
  alignItems: 'center',
  fontSize: config.textSizes.tertia,
  lineHeight: 1.2,
  justifyContent: justifyContent || 'center',
}));

const BodyCell = styled(HeaderCell)(({ theme: { config } }) => ({
  fontSize: config.textSizes.normal,
  textAlign: 'left',
}));

const Text = styled('div')({
  margin: '0.5rem 0.75rem',
  textTransform: 'capitalize',
});

const HeaderText = styled('div')({
  margin: '0.65rem 1rem',
});

const BodyRow = styled(HeaderRow)(({ theme: { palette } }) => ({
  minHeight: 'unset',
  '&:nth-child(2n + 1)': {
    backgroundColor: palette.background.connectorQuickView,
  },
  '&:nth-child(2n + 2)': {
    backgroundColor: palette.background.default,
  },
}));

const formatLimit = ({ limit, sublimit }: any) => {
  if (sublimit) {
    return moneyFormat(sublimit);
  }
  if (limit) {
    return moneyFormat(limit);
  }
  return null;
};

type PolicyHolderV6CoverageContext = {
  coverageMapping: Record<string, unknown> | undefined;
};

const CHECKBOX_ONLY_COVERAGES = new Set([
  'ADDITIONAL_INSURED',
  'TRIA',
  'BLANKET_ADDITIONAL_INSURED',
  'ADDITIONAL_NAMED_INSURED',
  'CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE',
  'CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT',
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME',
  'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE',
]);

const PRIME_100_COVERAGES_BY_ID = PRIME_100_COVERAGES.reduce<
  Record<string, { limitKey: string; deductibleKey: string }>
>((acc, cov) => {
  acc[cov.name] = { limitKey: cov.limitKey, deductibleKey: cov.deductibleKey };
  return acc;
}, {});

const usePolicyCoverages = (
  coverages: any,
  boostVersion?: string,
  v6Context?: PolicyHolderV6CoverageContext
) => {
  const removeKeywords = React.useCallback((word: any, keywords: any) => {
    return keywords.reduce((acc: any, keyword: any) => {
      return acc.replace(keyword, '');
    }, word);
  }, []);

  return React.useMemo(() => {
    const useV6 =
      boostVersion === BoostRaterVersionResponse.V6 ||
      (typeof boostVersion === 'string' && boostVersion.toUpperCase() === 'V6');

    if (useV6) {
      return buildV6Coverages(coverages, boostVersion, v6Context);
    }

    return buildV5Coverages(coverages, removeKeywords);
  }, [coverages, removeKeywords, boostVersion, v6Context]);
};

function buildV6Coverages(
  coverages: any,
  boostVersion: string | undefined,
  v6Context: PolicyHolderV6CoverageContext | undefined
) {
  const options = calculateCoverageOptionsV6(coverages ?? {}, {
    onChange: null,
    register: null,
    coverageMapping: v6Context?.coverageMapping ?? {},
    boostVersion: boostVersion ?? BoostRaterVersionResponse.V6,
  }) as {
    id?: string;
    label?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    mandatory?: boolean;
    percentage?: number;
  }[];

  const numericLimit =
    typeof coverages?.limit === 'number' && coverages.limit > 0
      ? coverages.limit
      : Number(coverages?.limit) || 1_000_000;

  const result: {
    coverageName: string;
    order: number;
    limit?: string | null;
    deductible?: string | null;
    checked?: boolean;
    waitingPeriod?: string | null;
    retroactivePeriod?: string | null;
  }[] = [];

  options.forEach((option, index) => {
    const enumId = typeof option.id === 'string' ? option.id : '';

    if (!option.label) {
      // Handle placeholder IDs that inject sub-coverage rows
      const subRows = buildSubCoverageRows(coverages, enumId, index);
      result.push(...subRows);
      return;
    }

    const isActive =
      option.mandatory || option.defaultChecked || option.checked;
    if (!isActive) return;

    const isCheckboxOnly = CHECKBOX_ONLY_COVERAGES.has(enumId);

    if (isCheckboxOnly) {
      result.push({
        coverageName: option.label,
        order: index,
        checked: true,
      });
      return;
    }

    const keys = PRIME_100_COVERAGES_BY_ID[enumId];
    const limitVal = keys?.limitKey ? coverages?.[keys.limitKey] : null;
    const deductibleVal = keys?.deductibleKey
      ? coverages?.[keys.deductibleKey]
      : null;

    // For coverages not in PRIME_100_COVERAGES (e.g. SECURITY_BREACH_EXPENSE,
    // PCI_FINES_AND_PENALTIES, etc.), derive limit from the percentage ratio
    // already computed by calculateCoverageOptionsV6 (percentage * aggregate limit).
    const resolvedLimit = limitVal
      ? moneyFormat(limitVal)
      : typeof option.percentage === 'number' && numericLimit
      ? moneyFormat(Math.round(option.percentage * numericLimit))
      : null;

    const deductible = deductibleVal != null ? moneyFormat(deductibleVal) : null;
    result.push({
      coverageName: option.label,
      order: index,
      limit: resolvedLimit,
      deductible,
    });
  });

  return result.sort((a, b) => a.order - b.order);
}

export function buildV5Coverages(coverages: any, removeKeywords: any) {
  const {
    waitingPeriod,
    retroactivePeriod,
    limit,
    deductible,
    ...restOfCoverages
  }: any = {
    ..._.pick(coverages, ['sublimit', ...getConsolidatedP100CoverageKeys()]),
  };

  const finalCoverages: Record<
    string,
    {
      order: number;
      limit?: string | null;
      deductible?: string | null;
      waitingPeriod?: string | null;
      retroactivePeriod?: string | null;
      checked?: boolean;
    }
  > = {};

  const StaticsCoverageMap = StaticsCoverages.reduce((acc, coverage) => {
    const coverageKey = removeKeywords(coverage.covLabel, [
      'SubLimit',
      'Limit',
      'Deductible',
      'Endorsement',
    ]);
    return { ...acc, [coverageKey]: { ...coverage } };
  }, {});

  function determineLimit(coverageKey: any) {
    if (coverageKey === 'businessIncomeCoverage') {
      return { limit: moneyFormat(restOfCoverages[coverageKey]) };
    }
    if (
      coverageKey === 'computerFraud' &&
      restOfCoverages[`${coverageKey}Endorsement`]
    ) {
      return { limit: moneyFormat(limit) };
    }
    return {
      limit: formatLimit({
        limit: restOfCoverages[`${coverageKey}Limit`],
        sublimit: restOfCoverages[`${coverageKey}SubLimit`],
      }),
    };
  }

  Object.keys(restOfCoverages).forEach((coverage) => {
    const coverageKey = removeKeywords(coverage, [
      'SubLimit',
      'Limit',
      'Deductible',
      'Endorsement',
    ]);

    if (coverageKey !== 'businessIncomeCoverage') {
      if (!restOfCoverages[`${coverageKey}Endorsement`]) return;
    }

    const staticEntry = StaticsCoverageMap[coverageKey];
    if (!staticEntry) return;

    const deductibleRaw = restOfCoverages[`${coverageKey}Deductible`];
    const deductible = deductibleRaw != null ? moneyFormat(deductibleRaw) : null;
    finalCoverages[staticEntry.name] = {
      order: staticEntry.order,
      deductible,
      ...determineLimit(coverageKey),
    };
  });

  StaticsCoverages.forEach((staticCoverage) => {
    if (staticCoverage.mandatory && !(staticCoverage.name in finalCoverages)) {
      finalCoverages[staticCoverage.name] = {
        order: staticCoverage.order,
        ...getCoverageLimit(limit, restOfCoverages, staticCoverage),
      };
    }
  });

  if (
    coverages?.additionalInsureds &&
    coverages?.additionalInsureds.length > 0
  ) {
    finalCoverages['Additional Insureds'] = { order: 99, checked: true };
  }

  if ('isTriaExcluded' in coverages && !coverages.isTriaExcluded) {
    finalCoverages[P100_COVERAGES.TRIA.name] = {
      order: P100_COVERAGES.TRIA.ordinal,
      checked: true,
    };
  }

  updateFinalCoveragesArrayForBoostV5(coverages, finalCoverages);

  const finalCoveragesArray = Object.keys(finalCoverages).map((key) => ({
    ...finalCoverages[key],
    coverageName: key,
  }));

  return finalCoveragesArray.sort(
    (first, second) => first.order - second.order
  );
}

function updateFinalCoveragesArrayForBoostV5(
  coverages: any,
  finalCoverages: Record<string, unknown>
) {
  if ('dedicatedBreachCostsEndorsementSubLimit' in coverages) {
    finalCoverages[P100_COVERAGES.DEDICATED_BREACH_COSTS_ENDORSEMENT.name] = {
      deductible: null,
      limit: moneyFormat(coverages.dedicatedBreachCostsEndorsementSubLimit),
      order: P100_COVERAGES.DEDICATED_BREACH_COSTS_ENDORSEMENT.ordinal,
    };
  }

  if ('serviceFraudAndCryptojackingSubLimit' in coverages) {
    finalCoverages[P100_COVERAGES.SERVICE_FRAUD_AND_CRYPTOJACKING.name] = {
      deductible: null,
      limit: moneyFormat(coverages.serviceFraudAndCryptojackingSubLimit),
      order: P100_COVERAGES.SERVICE_FRAUD_AND_CRYPTOJACKING.ordinal,
    };
  }

  if ('enhancedBusinessAndCbiWithSystemFailure' in coverages) {
    finalCoverages[
      P100_COVERAGES.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE.name
    ] = {
      order:
        P100_COVERAGES
          .ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE
          .ordinal,
      checked: !!coverages.enhancedBusinessAndCbiWithSystemFailure,
    };
  }

  if ('enhancedBusinessAndCbi' in coverages) {
    finalCoverages[
      P100_COVERAGES.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME.name
    ] = {
      order:
        P100_COVERAGES.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME.ordinal,
      checked: !!coverages.enhancedBusinessAndCbi,
    };
  }

  if ('reverseSocialEngineeringEndorsement' in coverages) {
    finalCoverages[P100_COVERAGES.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT.name] =
      {
        deductible: moneyFormat(coverages.reverseSocialEngineeringDeductible),
        limit: moneyFormat(coverages.reverseSocialEngineeringLimit),
        order: P100_COVERAGES.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT.ordinal,
      };
  }
}

function getCoverageLimit(
  aggregateLimit: any,
  restOfCoverages: any,
  staticCoverage: any
) {
  if (staticCoverage.covLabel === 'businessIncomeCoverage') {
    return {
      limit: moneyFormat(restOfCoverages[staticCoverage.covLabel]),
    };
  }

  if (staticCoverage.covLabel === 'extortionThreats') {
    return {
      limit: restOfCoverages.extortionThreatsAndRansomPayment
        ? restOfCoverages.extortionThreatsLimitDetail
        : moneyFormat(0.05 * aggregateLimit),
    };
  }

  return {
    limit:
      staticCoverage.covLabel === 'publicRelationsExpense'
        ? moneyFormat(0.05 * aggregateLimit)
        : moneyFormat(aggregateLimit),
  };
}
